import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import FilterTabs from '../components/admin/orders/FilterTabs';
import SearchAndFilters from '../components/admin/orders/SearchAndFilters';
import OrdersTable from '../components/admin/orders/OrdersTable';
import { supabase } from '../config/supabase';
import './OrdersManagement.css';

const OrdersManagement = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    New: 0,
    Preparing: 0,
    Ready: 0,
    Delivered: 0,
    Cancelled: 0
  });

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchOrders();
    fetchStatusCounts();
    const interval = setInterval(() => {
      fetchOrders(true);
      fetchStatusCounts();
    }, 30000); // Auto-refresh every 30 seconds

    return () => clearInterval(interval);
  }, [activeTab, searchQuery, currentPage]);

  const fetchStatusCounts = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('status');

      if (error) {
        console.error('Error fetching order counts:', error);
        return;
      }

      const statusMap = {
        'pending': 'New',
        'confirmed': 'Preparing',
        'preparing': 'Preparing',
        'ready': 'Ready',
        'out_for_delivery': 'Ready',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      };

      const counts = {
        All: data.length,
        New: 0,
        Preparing: 0,
        Ready: 0,
        Delivered: 0,
        Cancelled: 0
      };

      data.forEach(order => {
        const componentStatus = statusMap[order.status.toLowerCase()] || 'New';
        if (counts.hasOwnProperty(componentStatus)) {
          counts[componentStatus]++;
        }
      });

      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching order counts:', error);
    }
  };

  const fetchOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      // Build query
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by status
      if (activeTab !== 'All') {
        const statusMap = {
          'New': 'pending',
          'Preparing': ['confirmed', 'preparing'],
          'Ready': 'ready',
          'Delivered': 'delivered',
          'Cancelled': 'cancelled'
        };

        const dbStatus = statusMap[activeTab];
        if (Array.isArray(dbStatus)) {
          query = query.in('status', dbStatus);
        } else {
          query = query.eq('status', dbStatus);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
        setLastUpdated(new Date());
        if (!silent) setLoading(false);
        return;
      }

      // Transform Supabase data to component format
      const transformedOrders = (data || []).map((order, index) => {
        // Parse items if it's a string
        const orderItems = typeof order.items === 'string' 
          ? JSON.parse(order.items) 
          : order.items;

        // Map database status to component status
        const statusMap = {
          'pending': 'New',
          'confirmed': 'Preparing',
          'preparing': 'Preparing',
          'ready': 'Ready',
          'out_for_delivery': 'Ready',
          'delivered': 'Delivered',
          'cancelled': 'Cancelled'
        };

        const componentStatus = statusMap[order.status.toLowerCase()] || 'New';

        return {
          id: order.id,
          orderNumber: index + 1, // Sequential number starting from 1
          customer: {
            name: order.customer_name,
            phone: order.customer_phone || 'N/A'
          },
          items: orderItems.map(item => item.name || item),
          itemCount: orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
          amount: parseFloat(order.total),
          status: componentStatus,
          createdAt: new Date(order.created_at),
          deliveryAddress: order.delivery_address,
          paymentMethod: order.payment_method === 'cash' ? 'Cash on Delivery' : 'Card Payment',
          specialInstructions: order.delivery_instructions || null,
          // Include full order data for modal
          fullOrderData: order
        };
      });

      // Filter by search query
      let filtered = transformedOrders;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = transformedOrders.filter(order =>
          order.id.toLowerCase().includes(query) ||
          order.customer.name.toLowerCase().includes(query) ||
          order.customer.phone.toLowerCase().includes(query)
        );
      }

      setOrders(filtered);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };


  const handleOrderClick = (order) => {
    navigate(`/admin/orders/${order.id}`);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    // Map component status to database status
    const statusMap = {
      'New': 'pending',
      'Preparing': 'preparing',
      'Ready': 'ready',
      'Delivered': 'delivered',
      'Cancelled': 'cancelled'
    };

    const dbStatus = statusMap[newStatus] || 'pending';

    try {
      // Update in Supabase
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: dbStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) {
        console.error('Error updating order status:', error);
        alert('Failed to update order status. Please try again.');
        return;
      }

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Refresh orders and counts
      fetchOrders(true);
      fetchStatusCounts();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    }
  };


  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="orders-management">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="orders-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="orders-content">
          <div className="page-header">
            <h1 className="page-title">Orders Management</h1>
            <nav className="breadcrumb">
              <span>Dashboard</span>
              <span className="breadcrumb-separator">/</span>
              <span>Orders</span>
            </nav>
          </div>

          <FilterTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            statusCounts={statusCounts}
          />

          <SearchAndFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onRefresh={fetchOrders}
          />

          <OrdersTable
            orders={orders}
            loading={loading}
            onOrderClick={handleOrderClick}
            onStatusChange={handleStatusChange}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            lastUpdated={lastUpdated}
          />

        </main>
      </div>
    </div>
  );
};

export default OrdersManagement;

